import { useEffect, useMemo, useState } from 'react';
import { clsx } from 'clsx';
import { useQueryClient } from '@tanstack/react-query';
import { Loader2, CheckCircle, XCircle, AlertCircle, X } from 'lucide-react';
import { useGetErpConnection } from '@api/hooks/useErpConnection';
import { useWorkflowStatusSSE } from '@api/hooks/useWorkflowStatus';
import {
  getErpProviderLabel,
  isErpAuthProvider,
  redirectToErpAuthorization,
} from '@api/quickbooksAuth';
import { shouldReconnectQuickBooks as shouldReconnectQuickBooksError } from '@api/errorHandling';
import type { APPostingWorkflowState } from '@schemas/api';
import { useNotificationStore } from '@store/notificationStore';
import {
  type TrackedAPPostingWorkflow,
  useAPPostingWorkflowStore,
} from '@store/apPostingWorkflowStore';

const formatWorkflowLabel = (state?: APPostingWorkflowState | null, fallback?: string | null) => {
  const label = state?.stage || state?.status || fallback || 'waiting_for_workflow_updates';
  return label.replace(/_/g, ' ');
};

const PopupTone = ({
  workflowStatus,
}: {
  workflowStatus?: string | null;
}) => {
  if (workflowStatus === 'completed') {
    return <CheckCircle className="h-5 w-5 text-green-600" />;
  }

  if (
    workflowStatus === 'error'
    || workflowStatus === 'approval_rejected'
    || workflowStatus === 'posting_failed'
  ) {
    return <XCircle className="h-5 w-5 text-red-600" />;
  }

  if (workflowStatus === 'waiting_for_qb_reauth') {
    return <AlertCircle className="h-5 w-5 text-yellow-600" />;
  }

  return <Loader2 className="h-5 w-5 text-blue-600 animate-spin" />;
};

const APPostingWorkflowNotification = ({ workflow }: { workflow: TrackedAPPostingWorkflow }) => {
  const queryClient = useQueryClient();
  const { addNotification } = useNotificationStore();
  const removeWorkflow = useAPPostingWorkflowStore((state) => state.removeWorkflow);
  const { data: erpConnection } = useGetErpConnection();
  const [hasTriggeredReconnect, setHasTriggeredReconnect] = useState(false);
  const [terminalHandled, setTerminalHandled] = useState(false);

  const { status: workflowEvent, isConnected, isConnecting, error } = useWorkflowStatusSSE(
    workflow.workflowId
  );

  const liveWorkflowState = useMemo(
    () =>
      typeof workflowEvent?.status === 'object' && workflowEvent?.status
        ? (workflowEvent.status as APPostingWorkflowState)
        : null,
    [workflowEvent?.status]
  );

  const liveWorkflowStatus = typeof workflowEvent?.status === 'string'
    ? workflowEvent.status
    : liveWorkflowState?.status;
  const resultWorkflowStatus = typeof workflowEvent?.result === 'object' && workflowEvent?.result
    ? String(workflowEvent.result.status ?? '')
    : null;
  const effectiveWorkflowStatus = workflowEvent?.completed
    ? (resultWorkflowStatus || 'completed')
    : (liveWorkflowStatus || resultWorkflowStatus || null);

  const liveWorkflowError = workflowEvent?.error || liveWorkflowState?.error || null;

  const shouldReconnectQuickBooks = (
    liveWorkflowStatus === 'waiting_for_qb_reauth'
    || liveWorkflowState?.stage === 'awaiting_quickbooks_reauth'
    || shouldReconnectQuickBooksError({ message: liveWorkflowError } as Error)
  );
  const provider = isErpAuthProvider(erpConnection?.erp_type)
    ? erpConnection.erp_type
    : null;
  const providerLabel = getErpProviderLabel(erpConnection?.erp_type);

  useEffect(() => {
    if (terminalHandled) {
      return;
    }

    if (effectiveWorkflowStatus === 'completed') {
      setTerminalHandled(true);
      queryClient.invalidateQueries({ queryKey: ['ap-postings'] });
      addNotification({
        message: `AP posting ${workflow.invoiceNum} finalized successfully.`,
        type: 'success',
      });
      window.setTimeout(() => removeWorkflow(workflow.workflowId), 3500);
      return;
    }

    if (effectiveWorkflowStatus === 'discarded' || effectiveWorkflowStatus === 'expired') {
      setTerminalHandled(true);
      queryClient.invalidateQueries({ queryKey: ['ap-postings'] });
      addNotification({
        message:
          effectiveWorkflowStatus === 'expired'
            ? `AP posting ${workflow.invoiceNum} expired.`
            : `AP posting ${workflow.invoiceNum} discarded.`,
        type: effectiveWorkflowStatus === 'expired' ? 'warning' : 'success',
      });
      window.setTimeout(() => removeWorkflow(workflow.workflowId), 3500);
      return;
    }

    if (effectiveWorkflowStatus === 'error' || effectiveWorkflowStatus === 'approval_rejected') {
      setTerminalHandled(true);
      queryClient.invalidateQueries({ queryKey: ['ap-postings'] });
      addNotification({
        message: liveWorkflowError || `AP posting ${workflow.invoiceNum} workflow failed.`,
        type: 'error',
      });
      window.setTimeout(() => removeWorkflow(workflow.workflowId), 5000);
      return;
    }

    if (effectiveWorkflowStatus === 'posting_failed') {
      setTerminalHandled(true);
      queryClient.invalidateQueries({ queryKey: ['ap-postings'] });
      addNotification({
        message: liveWorkflowError
          ? `QuickBooks Desktop rejected invoice ${workflow.invoiceNum}: ${liveWorkflowError}`
          : `QuickBooks Desktop rejected invoice ${workflow.invoiceNum}.`,
        type: 'error',
      });
      window.setTimeout(() => removeWorkflow(workflow.workflowId), 5000);
      return;
    }
  }, [
    addNotification,
    effectiveWorkflowStatus,
    liveWorkflowError,
    queryClient,
    removeWorkflow,
    terminalHandled,
    workflow.invoiceNum,
    workflow.workflowId,
  ]);

  useEffect(() => {
    if (!shouldReconnectQuickBooks || hasTriggeredReconnect) {
      return;
    }

    setHasTriggeredReconnect(true);
    if (!provider) {
      addNotification({
        message: `${providerLabel} connection requires attention. Open Integrations to check its connector.`,
        type: 'warning',
      });
      return;
    }
    addNotification({
      message: `${providerLabel} connection expired. Redirecting to reconnect...`,
      type: 'warning',
    });

    const returnPath = `${window.location.pathname}${window.location.search}${window.location.hash}`;
    const timer = window.setTimeout(async () => {
      try {
        const redirected = await redirectToErpAuthorization(provider, {
          resumeWorkflowId: workflow.workflowId,
          returnPath,
        });
        if (!redirected) {
          addNotification({
            message: `${providerLabel} reconnection is unavailable. Open Integrations and reconnect manually.`,
            type: 'error',
          });
        }
      } catch {
        addNotification({
          message: `Failed to start ${providerLabel} reconnection. Open Integrations and reconnect manually.`,
          type: 'error',
        });
      }
    }, 800);

    return () => window.clearTimeout(timer);
  }, [
    addNotification,
    hasTriggeredReconnect,
    provider,
    providerLabel,
    shouldReconnectQuickBooks,
    workflow.workflowId,
  ]);

  const popupToneClass = effectiveWorkflowStatus === 'completed'
    ? 'border-green-200 bg-green-50 text-green-900'
    : effectiveWorkflowStatus === 'error'
      || effectiveWorkflowStatus === 'approval_rejected'
      || effectiveWorkflowStatus === 'posting_failed'
      ? 'border-red-200 bg-red-50 text-red-900'
      : effectiveWorkflowStatus === 'waiting_for_qb_reauth'
        ? 'border-yellow-200 bg-yellow-50 text-yellow-900'
        : 'border-blue-200 bg-blue-50 text-blue-900';

  return (
    <div
      className={clsx(
        'pointer-events-auto rounded-xl border shadow-lg px-4 py-3 backdrop-blur-sm',
        popupToneClass
      )}
    >
      <div className="flex items-start gap-3">
        <PopupTone workflowStatus={effectiveWorkflowStatus} />
        <div className="min-w-0 flex-1">
          <div className="flex items-center justify-between gap-3">
            <p className="text-sm font-semibold">
              {workflow.action === 'discard'
                ? 'Discarding'
                : workflow.action === 'correct'
                  ? 'Correcting'
                  : 'Posting'} invoice {workflow.invoiceNum}
            </p>
            <button
              type="button"
              onClick={() => removeWorkflow(workflow.workflowId)}
              className="rounded p-1 hover:bg-black/5 transition-colors"
              title="Dismiss"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
          <p className="mt-1 text-sm capitalize">
            {formatWorkflowLabel(
              workflowEvent?.completed ? null : liveWorkflowState,
              effectiveWorkflowStatus
            )}
          </p>
          {liveWorkflowError && (
            <p className="mt-1 text-xs opacity-90">{liveWorkflowError}</p>
          )}
          {!liveWorkflowError && error && (
            <p className="mt-1 text-xs opacity-90">{error}</p>
          )}
          <p className="mt-2 text-[11px] uppercase tracking-wide opacity-70">
            {isConnected ? 'Live updates' : isConnecting ? 'Connecting' : 'Reconnecting'}
          </p>
        </div>
      </div>
    </div>
  );
};

export const APPostingWorkflowNotifications = () => {
  const workflows = useAPPostingWorkflowStore((state) => state.workflows);

  if (workflows.length === 0) {
    return null;
  }

  return (
    <div className="pointer-events-none fixed bottom-4 right-4 z-[95] flex w-full max-w-md flex-col gap-3">
      {workflows.map((workflow) => (
        <APPostingWorkflowNotification key={workflow.workflowId} workflow={workflow} />
      ))}
    </div>
  );
};
